import {
    TEMPORAL_MODES,
    freezeWorkspaceSnapshot,
} from './workspace-store.js';

export const TIME_RESOLUTION = Object.freeze({
    EXACT: 'exact',
    NEAREST_WITHIN_TOLERANCE: 'nearestWithinTolerance',
    UNAVAILABLE: 'unavailable',
});

const VALID_MODES = new Set(Object.values(TEMPORAL_MODES));
const VALID_RESOLUTIONS = new Set(Object.values(TIME_RESOLUTION));

function epochMs(value) {
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric)) return numeric;
        const parsed = Date.parse(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function timeFromStep(step) {
    if (step == null) return null;
    if (typeof step !== 'object' || step instanceof Date) return epochMs(step);
    for (const key of ['validMs', 'valid_ms', 'time_ms', 'targetMs', 'valid_utc', 'validUtc', 'time', 'at']) {
        const parsed = epochMs(step[key]);
        if (parsed !== null) return parsed;
    }
    return null;
}

function finiteTolerance(value, fallback = 0) {
    const number = Number(value ?? fallback);
    return Number.isFinite(number) && number >= 0 ? number : Math.max(0, Number(fallback) || 0);
}

function unavailable(requestedMs, reason, coverage = null) {
    return freezeWorkspaceSnapshot({
        status: TIME_RESOLUTION.UNAVAILABLE,
        requestedMs,
        validMs: null,
        deltaMs: null,
        distanceMs: null,
        reason,
        coverage,
    });
}

function availableResolution(status, requestedMs, validMs, coverage) {
    const deltaMs = validMs - requestedMs;
    return freezeWorkspaceSnapshot({
        status,
        requestedMs,
        validMs,
        deltaMs,
        distanceMs: Math.abs(deltaMs),
        reason: null,
        coverage,
    });
}

export function normalizeTemporalCoverage(rawCoverage, options = {}) {
    if (rawCoverage === false || rawCoverage == null) {
        return freezeWorkspaceSnapshot({
            available: false,
            startMs: null,
            endMs: null,
            timestamps: [],
            toleranceMs: finiteTolerance(options.toleranceMs),
            reason: 'coverage-unavailable',
        });
    }

    const coverage = Array.isArray(rawCoverage) ? { timestamps: rawCoverage } : rawCoverage;
    if (!coverage || typeof coverage !== 'object') {
        return freezeWorkspaceSnapshot({
            available: false,
            startMs: null,
            endMs: null,
            timestamps: [],
            toleranceMs: finiteTolerance(options.toleranceMs),
            reason: 'invalid-coverage',
        });
    }

    const rawTimes = coverage.timestamps
        ?? coverage.validTimes
        ?? coverage.valid_times
        ?? coverage.timesteps
        ?? coverage.steps
        ?? [];
    const timestamps = [...new Set((Array.isArray(rawTimes) ? rawTimes : [])
        .map(timeFromStep)
        .filter(Number.isFinite))]
        .sort((a, b) => a - b);

    const explicitStart = epochMs(coverage.startMs
        ?? coverage.start_ms
        ?? coverage.start
        ?? coverage.fromMs
        ?? coverage.from);
    const explicitEnd = epochMs(coverage.endMs
        ?? coverage.end_ms
        ?? coverage.end
        ?? coverage.toMs
        ?? coverage.to);
    const startMs = explicitStart ?? timestamps[0] ?? null;
    const endMs = explicitEnd ?? timestamps[timestamps.length - 1] ?? null;
    const toleranceMs = finiteTolerance(coverage.toleranceMs ?? coverage.tolerance_ms, options.toleranceMs);

    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs > endMs) {
        return freezeWorkspaceSnapshot({
            available: false,
            startMs: null,
            endMs: null,
            timestamps: [],
            toleranceMs,
            reason: 'invalid-coverage',
        });
    }

    return freezeWorkspaceSnapshot({
        available: coverage.available !== false,
        startMs,
        endMs,
        timestamps: timestamps.filter(time => time >= startMs && time <= endMs),
        toleranceMs,
        reason: coverage.available === false ? 'coverage-unavailable' : null,
    });
}

export function resolveTimeAgainstCoverage(target, rawCoverage, options = {}) {
    const requestedMs = epochMs(target);
    if (!Number.isFinite(requestedMs)) return unavailable(null, 'invalid-target');

    const coverage = normalizeTemporalCoverage(rawCoverage, options);
    if (!coverage.available) return unavailable(requestedMs, coverage.reason || 'coverage-unavailable', coverage);

    // The declared interval is a hard boundary. A tolerance can select a nearby
    // frame inside it, but must never extend or clamp either edge.
    if (requestedMs < coverage.startMs || requestedMs > coverage.endMs) {
        return unavailable(requestedMs, 'outside-coverage', coverage);
    }

    if (!coverage.timestamps.length) {
        return availableResolution(TIME_RESOLUTION.EXACT, requestedMs, requestedMs, coverage);
    }

    let nearest = coverage.timestamps[0];
    let distance = Math.abs(nearest - requestedMs);
    for (let index = 1; index < coverage.timestamps.length; index += 1) {
        const candidate = coverage.timestamps[index];
        const candidateDistance = Math.abs(candidate - requestedMs);
        if (candidateDistance < distance) {
            nearest = candidate;
            distance = candidateDistance;
        }
    }
    if (distance === 0) return availableResolution(TIME_RESOLUTION.EXACT, requestedMs, nearest, coverage);
    if (distance <= coverage.toleranceMs) {
        return availableResolution(TIME_RESOLUTION.NEAREST_WITHIN_TOLERANCE, requestedMs, nearest, coverage);
    }
    return unavailable(requestedMs, 'no-step-within-tolerance', coverage);
}

export function deriveTemporalMode(target, liveReference, toleranceMs = 90_000) {
    const targetMs = epochMs(target);
    const liveMs = epochMs(liveReference);
    if (!Number.isFinite(targetMs) || !Number.isFinite(liveMs)) {
        throw new TypeError('A finite target and live reference are required');
    }
    const tolerance = finiteTolerance(toleranceMs, 90_000);
    if (targetMs < liveMs - tolerance) return TEMPORAL_MODES.HISTORY;
    if (targetMs > liveMs + tolerance) return TEMPORAL_MODES.FUTURE;
    return TEMPORAL_MODES.LIVE;
}

function mergeSegments(segments) {
    const sorted = segments
        .filter(segment => Number.isFinite(segment.startMs) && Number.isFinite(segment.endMs))
        .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
    const merged = [];
    for (const segment of sorted) {
        const previous = merged[merged.length - 1];
        if (!previous || segment.startMs > previous.endMs) {
            merged.push({ startMs: segment.startMs, endMs: segment.endMs });
        } else {
            previous.endMs = Math.max(previous.endMs, segment.endMs);
        }
    }
    return merged;
}

export function aggregateTemporalCoverages(entries = []) {
    const byProvider = {};
    const segments = [];
    const timestamps = new Set();
    for (const entry of entries) {
        const id = String(entry?.id ?? '').trim();
        if (!id) continue;
        const coverage = normalizeTemporalCoverage(entry.coverage, { toleranceMs: entry.toleranceMs });
        byProvider[id] = coverage;
        if (!coverage.available) continue;
        segments.push({ startMs: coverage.startMs, endMs: coverage.endMs });
        coverage.timestamps.forEach(time => timestamps.add(time));
    }
    const merged = mergeSegments(segments);
    return freezeWorkspaceSnapshot({
        available: merged.length > 0,
        startMs: merged.length ? merged[0].startMs : null,
        endMs: merged.length ? merged[merged.length - 1].endMs : null,
        segments: merged,
        timestamps: [...timestamps].sort((a, b) => a - b),
        byProvider,
    });
}

function abortController() {
    if (typeof globalThis.AbortController === 'function') return new globalThis.AbortController();
    const signal = { aborted: false, addEventListener() {}, removeEventListener() {} };
    return { signal, abort() { signal.aborted = true; } };
}

function providerId(provider) {
    const id = String(provider?.id ?? '').trim();
    if (!id) throw new TypeError('Temporal provider id must be a non-empty string');
    return id;
}

function normalizeCustomResolution(value, requestedMs, coverage, fallback) {
    if (value === undefined) return fallback;
    if (value === null || value === false) return unavailable(requestedMs, 'provider-unavailable', coverage);
    if (typeof value === 'number' || typeof value === 'string' || value instanceof Date) {
        const validMs = epochMs(value);
        if (!Number.isFinite(validMs)) return unavailable(requestedMs, 'provider-invalid-resolution', coverage);
        if (validMs < coverage.startMs || validMs > coverage.endMs) {
            return unavailable(requestedMs, 'resolved-outside-coverage', coverage);
        }
        const distance = Math.abs(validMs - requestedMs);
        if (distance === 0) return availableResolution(TIME_RESOLUTION.EXACT, requestedMs, validMs, coverage);
        if (distance <= coverage.toleranceMs) {
            return availableResolution(TIME_RESOLUTION.NEAREST_WITHIN_TOLERANCE, requestedMs, validMs, coverage);
        }
        return unavailable(requestedMs, 'no-step-within-tolerance', coverage);
    }
    if (typeof value !== 'object' || !VALID_RESOLUTIONS.has(value.status)) {
        return unavailable(requestedMs, 'provider-invalid-resolution', coverage);
    }
    if (value.status === TIME_RESOLUTION.UNAVAILABLE) {
        return unavailable(requestedMs, value.reason || 'provider-unavailable', coverage);
    }
    const validMs = epochMs(value.validMs ?? value.valid_ms ?? value.valid_utc);
    if (!Number.isFinite(validMs) || validMs < coverage.startMs || validMs > coverage.endMs) {
        return unavailable(requestedMs, 'resolved-outside-coverage', coverage);
    }
    const distance = Math.abs(validMs - requestedMs);
    if (distance === 0) return availableResolution(TIME_RESOLUTION.EXACT, requestedMs, validMs, coverage);
    if (distance <= coverage.toleranceMs) {
        return availableResolution(TIME_RESOLUTION.NEAREST_WITHIN_TOLERANCE, requestedMs, validMs, coverage);
    }
    return unavailable(requestedMs, 'no-step-within-tolerance', coverage);
}

function readCoverage(provider, context) {
    const raw = typeof provider.coverage === 'function'
        ? provider.coverage(context)
        : provider.coverage;
    if (raw && typeof raw.then === 'function') {
        throw new TypeError(`Temporal provider ${provider.id} coverage must be synchronous`);
    }
    return normalizeTemporalCoverage(raw, { toleranceMs: provider.toleranceMs });
}

function errorMessage(error) {
    return String(error?.message || error || 'unknown provider error');
}

export function createTimeController({
    store,
    liveReference = () => Date.now(),
    liveToleranceMs = 90_000,
    syncLayerState = true,
} = {}) {
    if (!store || typeof store.getState !== 'function' || typeof store.setTime !== 'function') {
        throw new TypeError('Time controller requires a workspace store');
    }
    if (typeof liveReference !== 'function') throw new TypeError('liveReference must be a function');

    const providers = new Map();
    const listeners = new Set();
    let sequence = 0;
    let activeAbort = null;
    let lastResult = null;
    let destroyed = false;

    const notify = event => [...listeners].forEach(listener => listener(event));
    const contextFor = targetMs => ({ state: store.getState(), targetMs });

    const getCoverage = () => {
        const targetMs = store.getState().targetMs;
        const entries = [];
        for (const provider of providers.values()) {
            try {
                entries.push({ id: provider.id, coverage: readCoverage(provider, contextFor(targetMs)) });
            } catch (error) {
                entries.push({
                    id: provider.id,
                    coverage: normalizeTemporalCoverage(false),
                    error: errorMessage(error),
                });
            }
        }
        return aggregateTemporalCoverages(entries);
    };

    const registerProvider = (rawProvider, options = {}) => {
        if (destroyed) throw new Error('Time controller has been destroyed');
        const id = providerId(rawProvider);
        if (providers.has(id) && !options.replace) throw new Error(`Temporal provider already registered: ${id}`);
        const provider = {
            ...rawProvider,
            id,
            toleranceMs: finiteTolerance(rawProvider.toleranceMs),
        };
        providers.set(id, provider);
        notify(freezeWorkspaceSnapshot({ type: 'provider-registered', id, coverage: getCoverage() }));
        return () => {
            if (providers.get(id) !== provider) return false;
            providers.delete(id);
            notify(freezeWorkspaceSnapshot({ type: 'provider-unregistered', id, coverage: getCoverage() }));
            return true;
        };
    };

    const resolveProvider = async (id, target = store.getState().targetMs, extraContext = {}) => {
        const provider = providers.get(String(id));
        if (!provider) throw new Error(`Unknown temporal provider: ${String(id)}`);
        const requestedMs = epochMs(target);
        if (!Number.isFinite(requestedMs)) return unavailable(null, 'invalid-target');
        const coverage = readCoverage(provider, contextFor(requestedMs));
        const fallback = resolveTimeAgainstCoverage(requestedMs, coverage);
        if (fallback.status === TIME_RESOLUTION.UNAVAILABLE && fallback.reason === 'outside-coverage') return fallback;
        if (typeof provider.resolve !== 'function') return fallback;
        const custom = await provider.resolve(requestedMs, {
            ...extraContext,
            providerId: provider.id,
            coverage,
            defaultResolution: fallback,
            state: store.getState(),
        });
        return normalizeCustomResolution(custom, requestedMs, coverage, fallback);
    };

    const setTarget = async (target, options = {}) => {
        if (destroyed) throw new Error('Time controller has been destroyed');
        const targetMs = epochMs(target);
        if (!Number.isFinite(targetMs)) throw new TypeError('Time target must be a finite epoch timestamp');
        const liveMs = epochMs(options.liveMs ?? liveReference());
        if (!Number.isFinite(liveMs)) throw new TypeError('Live reference must be a finite epoch timestamp');
        const mode = options.mode ?? deriveTemporalMode(targetMs, liveMs, liveToleranceMs);
        if (!VALID_MODES.has(mode)) throw new TypeError(`Unknown temporal mode: ${String(mode)}`);

        const requestSequence = ++sequence;
        activeAbort?.abort();
        const requestAbort = abortController();
        activeAbort = requestAbort;
        store.setTime(targetMs, mode);

        const started = freezeWorkspaceSnapshot({
            type: 'started',
            sequence: requestSequence,
            targetMs,
            mode,
            liveMs,
            origin: options.origin || 'workspace',
        });
        notify(started);

        const providerEntries = [...providers.values()];
        const resultEntries = await Promise.all(providerEntries.map(async provider => {
            let resolution;
            let applied = false;
            let error = null;
            try {
                resolution = await resolveProvider(provider.id, targetMs, {
                    sequence: requestSequence,
                    mode,
                    liveMs,
                    signal: requestAbort.signal,
                    isCurrent: () => sequence === requestSequence && !requestAbort.signal.aborted,
                });
                if (sequence === requestSequence && !requestAbort.signal.aborted && typeof provider.apply === 'function') {
                    await provider.apply(resolution, {
                        sequence: requestSequence,
                        targetMs,
                        mode,
                        liveMs,
                        signal: requestAbort.signal,
                        state: store.getState(),
                        isCurrent: () => sequence === requestSequence && !requestAbort.signal.aborted,
                    });
                    applied = true;
                }
            } catch (caught) {
                error = errorMessage(caught);
                resolution = unavailable(targetMs, 'provider-error');
            }
            return [provider.id, freezeWorkspaceSnapshot({ resolution, applied, error })];
        }));

        const stale = sequence !== requestSequence || requestAbort.signal.aborted;
        const providerResults = Object.fromEntries(resultEntries);
        const result = freezeWorkspaceSnapshot({
            type: stale ? 'stale' : 'committed',
            sequence: requestSequence,
            stale,
            targetMs,
            mode,
            liveMs,
            origin: options.origin || 'workspace',
            providers: providerResults,
        });

        if (!stale) {
            lastResult = result;
            if (syncLayerState && typeof store.patchLayers === 'function' && resultEntries.length) {
                const patches = {};
                resultEntries.forEach(([id, providerResult]) => {
                    const resolution = providerResult.resolution;
                    patches[id] = {
                        temporal: {
                            sequence: requestSequence,
                            requestedMs: targetMs,
                            mode,
                            status: resolution.status,
                            validMs: resolution.validMs,
                            deltaMs: resolution.deltaMs,
                            reason: resolution.reason,
                            error: providerResult.error,
                        },
                    };
                });
                store.patchLayers(patches);
            }
        }
        notify(result);
        return result;
    };

    const resetLive = (options = {}) => {
        const liveMs = epochMs(options.liveMs ?? liveReference());
        if (!Number.isFinite(liveMs)) throw new TypeError('Live reference must be a finite epoch timestamp');
        return setTarget(liveMs, {
            ...options,
            liveMs,
            mode: TEMPORAL_MODES.LIVE,
            origin: options.origin || 'live-reset',
        });
    };

    return Object.freeze({
        getState: store.getState,
        getCoverage,
        getLastResult: () => lastResult,
        getSequence: () => sequence,
        getProviders: () => freezeWorkspaceSnapshot([...providers.values()].map(provider => ({
            id: provider.id,
            toleranceMs: provider.toleranceMs,
        }))),
        registerProvider,
        resolveProvider,
        setTarget,
        resetLive,
        subscribe(listener) {
            if (typeof listener !== 'function') throw new TypeError('Time listener must be a function');
            if (destroyed) throw new Error('Time controller has been destroyed');
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        destroy() {
            if (destroyed) return;
            destroyed = true;
            sequence += 1;
            activeAbort?.abort();
            providers.clear();
            listeners.clear();
        },
    });
}
